import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Bell, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/notifications/mine', { params: { limit: 25 } });
      setNotifs(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);   // poll every minute
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markRead = async (n) => {
    try {
      await api.post(`/api/notifications/${n.id}/read`);
      await load();
    } catch (e) { /* silent */ }
    if (n.related_type === 'customer' && n.related_id) {
      setOpen(false);
      navigate('/customers');
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      toast.success('All notifications marked read');
      await load();
    } catch (e) {
      toast.error('Failed to mark all read');
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="notif-bell">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((o) => !o)}
        className="relative"
        data-testid="notif-bell-btn"
      >
        <Bell size={18} weight={unread > 0 ? 'fill' : 'regular'} className={unread > 0 ? 'text-orange-500' : 'text-zinc-600'} />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-white text-[9px] flex items-center justify-center font-bold"
          >{unread > 99 ? '99+' : unread}</span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[70vh] overflow-y-auto bg-white border border-zinc-300 shadow-lg rounded-sm z-50">
          <div className="flex items-center justify-between p-3 border-b border-zinc-200 bg-zinc-50">
            <h3 className="text-sm font-bold text-zinc-950">Notifications</h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-orange-600 hover:underline"
                data-testid="mark-all-read-btn"
              ><CheckCircle size={12} className="inline mr-1" />Mark all read</button>
            )}
          </div>
          <div>
            {notifs.length === 0 ? (
              <div className="p-6 text-center text-zinc-400 text-sm">No notifications yet</div>
            ) : notifs.map((n) => (
              <div
                key={n.id}
                onClick={() => markRead(n)}
                data-testid={`notif-row-${n.id}`}
                className={`p-3 border-b border-zinc-100 cursor-pointer hover:bg-orange-50 ${n.read_at ? 'opacity-60' : 'bg-orange-50/30'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs font-bold text-zinc-950">{n.title}</div>
                  {!n.read_at && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0 mt-1" />}
                </div>
                <div className="text-xs text-zinc-600 mt-1">{n.message}</div>
                <div className="text-[10px] text-zinc-400 mt-1">{n.created_at?.slice(0, 16).replace('T', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
